import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SearchableSelect from "@/components/SearchableSelect";
import DateField from "@/components/DateField";
import { fmtDate } from "@/lib/constants";

const TYPE_LABEL = { sakit: "Sakit", izin: "Izin", cuti: "Cuti" };

/**
 * InputStatusStafPanel — 2026-10-16 (user request). User ber-aksen
 * `attendance.enter_for_others` (diatur di Manajemen Pengguna → Access Control
 * → Kehadiran) dapat menginput status kehadiran staf lain, mis. mencatat rekan
 * sedang sakit. Entry langsung approved & tercermin di rekap kehadiran.
 */
export default function InputStatusStafPanel() {
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ user_id: "", leave_type: "sakit", start_date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [delBusy, setDelBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/attendance/proxy-status").then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat entri status."));
  }, []);
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [load]);

  const submit = async () => {
    if (!form.user_id) { toast.error("Pilih staf tujuan."); return; }
    if (!form.start_date || !form.end_date) { toast.error("Tanggal mulai & selesai wajib diisi."); return; }
    if (!(form.reason || "").trim()) { toast.error("Alasan wajib diisi."); return; }
    setBusy(true);
    try {
      await api.post("/attendance/proxy-status", { ...form, reason: form.reason.trim() });
      toast.success("Status kehadiran staf tercatat.");
      setForm({ user_id: "", leave_type: "sakit", start_date: "", end_date: "", reason: "" });
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal mencatat status.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    setDelBusy(id);
    try {
      await api.delete(`/attendance/proxy-status/${id}`);
      toast.success("Entri status dibatalkan.");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal membatalkan entri.");
    } finally { setDelBusy(null); }
  };

  const userOpts = users.map((u) => ({ value: u.id, label: `${u.name}${u.division ? ` · ${u.division}` : ""}` }));

  return (
    <div className="space-y-4" data-testid="att-proxy-status-panel">
      <div className="border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <UserPlus size={14} className="text-[#0B1D3A]" />
          <h3 className="text-sm font-semibold text-[#0B1D3A]">Input Status Kehadiran Staf Lain</h3>
        </div>
        <p className="text-[10px] text-slate-500" data-testid="att-proxy-hint">
          Catat status kehadiran staf lain (Sakit / Izin / Cuti) — mis. rekan melapor sakit via telepon. Entri langsung berlaku dan tampil di rekap kehadiran staf bersangkutan.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
          <div className="lg:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Staf *</label>
            <SearchableSelect value={form.user_id} onChange={(v) => setForm({ ...form, user_id: v })}
              options={userOpts} placeholder="Pilih staf…" testid="att-proxy-user" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Status *</label>
            <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
              data-testid="att-proxy-type"
              className="w-full border border-slate-300 px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0B1D3A]">
              <option value="sakit">Sakit</option>
              <option value="izin">Izin</option>
              <option value="cuti">Cuti</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Tgl Mulai *</label>
            <DateField testid="att-proxy-start" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Tgl Selesai *</label>
            <DateField testid="att-proxy-end" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Alasan *</label>
          <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
            data-testid="att-proxy-reason" className="rounded-none text-xs" placeholder="mis. Lapor sakit via telepon pagi ini" />
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy} data-testid="att-proxy-submit"
            className="rounded-none bg-[#0B1D3A] hover:bg-[#12284d] text-white text-xs uppercase tracking-wider">
            {busy ? <Loader2 size={12} className="animate-spin" /> : "Catat Status"}
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-xs min-w-[720px]">
          <thead>
            <tr className="text-left bg-slate-50 border-b-2 border-slate-300">
              <th className="px-2 py-2 overline text-slate-700">Staf</th>
              <th className="px-2 py-2 overline text-slate-700">Status</th>
              <th className="px-2 py-2 overline text-slate-700">Periode</th>
              <th className="px-2 py-2 overline text-slate-700">Alasan</th>
              <th className="px-2 py-2 overline text-slate-700">Dicatat Oleh</th>
              <th className="px-2 py-2 overline text-slate-700 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="att-proxy-tbody">
            {(rows || []).map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`att-proxy-row-${r.id}`}>
                <td className="px-2 py-1.5 font-semibold text-slate-800">{r.user_name || "—"}</td>
                <td className="px-2 py-1.5">
                  <span className={`inline-block px-1.5 py-px text-[10px] font-bold uppercase tracking-wider ${r.leave_type === "sakit" ? "bg-rose-100 text-rose-800" : r.leave_type === "izin" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}
                    data-testid={`att-proxy-type-label-${r.id}`}>{TYPE_LABEL[r.leave_type] || r.leave_type}</span>
                </td>
                <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">{fmtDate(r.start_date)} — {fmtDate(r.end_date)}</td>
                <td className="px-2 py-1.5 text-slate-600 break-words">{r.reason}</td>
                <td className="px-2 py-1.5 text-slate-600">{r.entered_by_name || "—"}</td>
                <td className="px-2 py-1.5 text-right">
                  <button type="button" onClick={() => del(r.id)} disabled={delBusy === r.id}
                    data-testid={`att-proxy-del-${r.id}`} title="Batalkan entri status ini"
                    className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-40">
                    {delBusy === r.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  </button>
                </td>
              </tr>
            ))}
            {rows !== null && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[11px] text-slate-400 italic" data-testid="att-proxy-empty">
                Belum ada entri status yang dicatat.
              </td></tr>
            )}
            {rows === null && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[11px] text-slate-400" data-testid="att-proxy-loading">
                <Loader2 className="inline animate-spin mr-2" size={13} /> Memuat…
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
